import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Shield, ChevronRight, Trash2 } from 'lucide-react';
import { useFetchRoles } from '../../services/roles';
import RoleEditorPanel from './RoleEditorPanel';
import CreateRoleModal from './CreateRoleModal';
import DeleteRoleDialog from './DeleteRoleDialog';

const PROTECTED_ROLES = ['admin'];

const RoleDefinitionsTab = () => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const { data: roles, isLoading } = useFetchRoles();

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedRole(null);
  };

  const handleDeleteClick = (e, role) => {
    e.stopPropagation();
    setRoleToDelete(role);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries(['roles']);
  };

  return (
    <div className="mx-auto" style={{ maxWidth: '1200px' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-1">
            Role Definitions
          </h2>
          <p className="text-sm text-slate-500 mb-0">
            Create and manage role definitions and their permission assignments.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Role
        </button>
      </div>

      {/* Roles List */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {!isLoading && roles?.length === 0 && (
          <div className="text-center py-12">
            <Shield className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No roles defined</p>
          </div>
        )}

        {!isLoading && roles?.length > 0 && (
          <div className="divide-y divide-slate-100">
            {roles.map((role) => {
              const isProtected = PROTECTED_ROLES.includes(role.roleName) || role.roleIsSystem;
              const permissionCount = role.permissions?.length || 0;

              return (
                <div
                  key={role.roleId}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleRoleClick(role)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRoleClick(role)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors group cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-slate-900">
                        {role.roleDisplayName}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        {role.roleName}
                      </span>
                      {isProtected && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                          <Shield className="h-3 w-3" /> Protected
                        </span>
                      )}
                      {permissionCount > 0 && (
                        <span className="text-xs text-slate-400 bg-slate-100 rounded-full px-2 py-0.5">
                          {permissionCount} {permissionCount === 1 ? 'permission' : 'permissions'}
                        </span>
                      )}
                    </div>
                    {role.roleDescription && (
                      <p className="text-xs text-slate-500 mb-0 truncate">
                        {role.roleDescription}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {!isProtected && (
                      <button
                        onClick={(e) => handleDeleteClick(e, role)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete role"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Editor Panel */}
      <RoleEditorPanel
        show={showEditor}
        onHide={handleCloseEditor}
        role={selectedRole}
        onSuccess={handleSuccess}
      />

      {/* Create Modal */}
      <CreateRoleModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={handleSuccess}
      />

      {/* Delete Dialog */}
      <DeleteRoleDialog
        show={!!roleToDelete}
        onHide={() => setRoleToDelete(null)}
        role={roleToDelete}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default RoleDefinitionsTab;
